/*
 * La Bésneta — fecha estimada de entrega.
 *
 * Por qué el cálculo va en el cliente y no en Liquid: el mensaje cambia dos veces al día (a la hora
 * de corte y a medianoche). Calculado en el servidor se congelaría en el render, y eso se rompe con
 * la caché del CDN, la bfcache, la recarga parcial vía Section Rendering API y —el caso más común—
 * una pestaña que el usuario deja abierta y cruza el corte. Ese último no depende de ninguna caché.
 *
 * Toda la aritmética de fechas usa Date.UTC(...) anclado a mediodía. Nunca se usa setDate() sobre un
 * Date local: eso arrastra el horario de verano y puede saltar de día. El ancla a mediodía deja 12h
 * de margen a cada lado, así que ningún desfase horario cambia la fecha resultante.
 */
(function () {
  'use strict';

  var DIAS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  var UN_DIA = 86400000;
  var TOPE_ITERACIONES = 60; // ~2 meses; evita colgar la pestaña con una configuración imposible
  var MARGEN_RELOJ_MS = 7 * UN_DIA;

  /* ------------------------------------------------------------------ utilidades de fecha */

  function aClaveISO(ts) {
    var d = new Date(ts);
    var m = String(d.getUTCMonth() + 1).padStart(2, '0');
    var dia = String(d.getUTCDate()).padStart(2, '0');
    return d.getUTCFullYear() + '-' + m + '-' + dia;
  }

  function diaSemana(ts) {
    return DIAS[new Date(ts).getUTCDay()];
  }

  /*
   * Avanza día a día mientras la fecha caiga en un día no permitido o esté excluida.
   * Devuelve null si agota el tope, para que quien llame se quede con el texto de reserva en vez
   * de mostrar una fecha inventada.
   */
  function rodar(ts, permitidos, excluidas) {
    for (var i = 0; i <= TOPE_ITERACIONES; i++) {
      if (permitidos[diaSemana(ts)] && excluidas.indexOf(aClaveISO(ts)) === -1) return ts;
      ts += UN_DIA;
    }
    return null;
  }

  function proximoDiaSemana(ts, nombreDia) {
    var objetivo = DIAS.indexOf(nombreDia);
    if (objetivo === -1) return null;
    // Siempre estrictamente posterior: "los pedidos del jueves se entregan el lunes" nunca puede
    // resolverse en el mismo día del pedido.
    for (var i = 1; i <= 7; i++) {
      var cand = ts + i * UN_DIA;
      if (new Date(cand).getUTCDay() === objetivo) return cand;
    }
    return null;
  }

  function alMenosUno(mapa) {
    for (var k in mapa) if (mapa[k]) return true;
    return false;
  }

  /* ------------------------------------------------------------------ cálculo puro */

  /*
   * Función pura: sin DOM, sin Date.now(). Recibe la fecha civil del pedido en hora de tienda y
   * las reglas; devuelve las dos fechas de entrega. Separarla así es lo que permite probar
   * cualquier día de la semana desde la consola sin esperar a que llegue.
   *
   * civil  = { Y, M (1-12), D, h, m }
   * reglas = { cutoffHour, cutoffMinute, dispatch{}, delivery{}, fixed{}, transitMin, transitMax,
   *            maxTotalDays, excluded[] }
   */
  function calcular(civil, reglas) {
    if (!alMenosUno(reglas.dispatch)) return { error: 'sin-dias-de-salida' };
    if (!alMenosUno(reglas.delivery)) return { error: 'sin-dias-de-reparto' };

    var transitMin = Math.max(0, reglas.transitMin);
    var transitMax = Math.max(transitMin, reglas.transitMax); // config invertida: se corrige
    var excluidas = reglas.excluded || [];

    var d0 = Date.UTC(civil.Y, civil.M - 1, civil.D, 12, 0, 0);

    // Entrega fija: manda sobre todo lo demás. Es la regla "los pedidos del jueves y viernes se
    // entregan el lunes", donde da igual cuándo salga de fábrica.
    var diaFijo = reglas.fixed[diaSemana(d0)];
    if (diaFijo) {
      var objetivo = proximoDiaSemana(d0, diaFijo);
      if (objetivo === null) return { error: 'dia-fijo-invalido' };
      var fija = rodar(objetivo, reglas.delivery, excluidas);
      if (fija === null) return { error: 'sin-fecha-posible' };
      return { salida: null, min: fija, max: fija, colapsa: true, motivo: 'entrega-fija' };
    }

    var minutos = civil.h * 60 + civil.m;
    var corte = reglas.cutoffHour * 60 + reglas.cutoffMinute;
    var salida = minutos < corte ? d0 : d0 + UN_DIA;

    // Se reintenta con salidas sucesivas si el tránsito se estira más de lo tolerable: un festivo
    // puede dejar el paquete días en camino, y esto es comida congelada.
    for (var intento = 0; intento <= 7; intento++) {
      salida = rodar(salida, reglas.dispatch, excluidas);
      if (salida === null) return { error: 'sin-fecha-posible' };

      var min = rodar(salida + transitMin * UN_DIA, reglas.delivery, excluidas);
      var max = rodar(salida + transitMax * UN_DIA, reglas.delivery, excluidas);
      if (min === null || max === null) return { error: 'sin-fecha-posible' };

      var totalDias = Math.round((max - salida) / UN_DIA);
      if (totalDias <= reglas.maxTotalDays) {
        return {
          salida: salida,
          min: min,
          max: max,
          colapsa: min === max,
          motivo: intento > 0 ? 'salida-aplazada' : 'normal'
        };
      }
      salida += UN_DIA; // esta salida estira demasiado el tránsito: se prueba la siguiente
    }
    return { error: 'transito-demasiado-largo' };
  }

  /* ------------------------------------------------------------------ formato */

  function formatear(ts, locale, conMes) {
    var opciones = conMes
      ? { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }
      : { weekday: 'long', day: 'numeric', timeZone: 'UTC' };
    try {
      return new Intl.DateTimeFormat(locale, opciones).format(new Date(ts));
    } catch (e) {
      return null;
    }
  }

  /* ------------------------------------------------------------------ custom element */

  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      this.salida = this.querySelector('[data-de-output]') || this;
      this.reglas = this.leerReglas();
      this.locale = document.documentElement.lang || 'es';
      this.render();

      // Una pestaña abierta puede cruzar la hora de corte o la medianoche. Al volver a ella se
      // recalcula: es justo el caso que el render en servidor no puede cubrir.
      this._alVolver = function () {
        if (!document.hidden) this.render();
      }.bind(this);
      document.addEventListener('visibilitychange', this._alVolver);
    }

    disconnectedCallback() {
      if (this._alVolver) document.removeEventListener('visibilitychange', this._alVolver);
    }

    leerReglas() {
      var d = this.dataset;
      var mapa = function (prefijo) {
        var out = {};
        DIAS.forEach(function (dia) {
          out[dia] = d[prefijo + dia.charAt(0).toUpperCase() + dia.slice(1)] === 'true';
        });
        return out;
      };
      var fijos = {};
      DIAS.forEach(function (dia) {
        var v = d['fixed' + dia.charAt(0).toUpperCase() + dia.slice(1)];
        fijos[dia] = v && v !== 'none' ? v : null;
      });
      return {
        cutoffHour: parseInt(d.cutoffHour, 10) || 0,
        cutoffMinute: parseInt(d.cutoffMinute, 10) || 0,
        dispatch: mapa('dispatch'),
        delivery: mapa('delivery'),
        fixed: fijos,
        transitMin: parseInt(d.transitMin, 10) || 0,
        transitMax: parseInt(d.transitMax, 10) || 0,
        maxTotalDays: parseInt(d.maxTotalDays, 10) || 99,
        excluded: (d.excluded || '')
          .split(/[\n,]/)
          .map(function (s) { return s.trim(); })
          .filter(function (s) { return /^\d{4}-\d{2}-\d{2}$/.test(s); })
      };
    }

    /*
     * Hora de tienda a partir del reloj del visitante y el offset que el servidor horneó en el HTML
     * (mismo patrón que CountdownTimer). Si el reloj del visitante está mal puesto —por detrás del
     * servidor o más de una semana por delante— se usa la hora del servidor. La caché desfasa horas,
     * nunca días, así que el umbral distingue caché de reloj roto.
     */
    civilAhora() {
      var m = (this.dataset.timezoneOffset || '').match(/([+-]?)(\d{2})(\d{2})/);
      var offset = m
        ? (m[1] === '-' ? -1 : 1) * ((parseInt(m[2], 10) * 60 + parseInt(m[3], 10)) * 60000)
        : 0;

      // Timestamp desplazado: sus getters UTC dan la hora civil de la tienda.
      var ahora = new Date();
      var ts = ahora.getTime() + ahora.getTimezoneOffset() * 60000 + offset;

      // data-server-now es epoch UTC en segundos; se le aplica el mismo offset para poder
      // compararlo con ts en la misma escala.
      var servidorSeg = parseInt(this.dataset.serverNow, 10);
      if (servidorSeg) {
        var servidorCivil = servidorSeg * 1000 + offset;
        var porDetras = ts < servidorCivil - 60000;
        var demasiadoPorDelante = ts > servidorCivil + MARGEN_RELOJ_MS;
        if (porDetras || demasiadoPorDelante) ts = servidorCivil;
      }

      var d = new Date(ts);
      return { Y: d.getUTCFullYear(), M: d.getUTCMonth() + 1, D: d.getUTCDate(), h: d.getUTCHours(), m: d.getUTCMinutes() };
    }

    texto(res) {
      if (res.error) return null;
      var cruzaMes = new Date(res.min).getUTCMonth() !== new Date(res.max).getUTCMonth();
      var conMes = this.dataset.dateFormat === 'weekday_day_month' || (this.dataset.dateFormat !== 'weekday_day' && cruzaMes);

      if (res.colapsa) {
        var f = formatear(res.min, this.locale, conMes);
        if (!f) return null;
        return (this.dataset.messageSingle || '').replace('{fecha}', f);
      }
      var fmin = formatear(res.min, this.locale, conMes);
      var fmax = formatear(res.max, this.locale, conMes);
      if (!fmin || !fmax) return null;
      return (this.dataset.messageRange || '').replace('{fecha_min}', fmin).replace('{fecha_max}', fmax);
    }

    /* Calcula y devuelve el resultado SIN tocar el DOM. Para probar desde la consola. */
    preview(iso) {
      var civil;
      if (iso) {
        var p = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?$/);
        if (!p) return { error: 'formato-esperado-AAAA-MM-DDTHH:MM' };
        civil = { Y: +p[1], M: +p[2], D: +p[3], h: +(p[4] || 0), m: +(p[5] || 0) };
      } else {
        civil = this.civilAhora();
      }
      var res = calcular(civil, this.reglas);
      return {
        pedido: civil,
        salida: res.salida ? aClaveISO(res.salida) : null,
        min: res.min ? aClaveISO(res.min) : null,
        max: res.max ? aClaveISO(res.max) : null,
        colapsa: res.colapsa,
        motivo: res.motivo,
        error: res.error,
        texto: this.texto(res)
      };
    }

    /* Igual que preview() pero pinta el resultado, para hacer capturas. */
    simulate(iso) {
      var r = this.preview(iso);
      if (r.texto) this.salida.textContent = r.texto;
      return r;
    }

    render() {
      var res = calcular(this.civilAhora(), this.reglas);
      var txt = this.texto(res);
      // Si algo falla se deja intacto el texto de reserva que pintó Liquid: mejor una promesa
      // genérica y cierta que una fecha inventada.
      if (txt) this.salida.textContent = txt;

      if (this.dataset.debug === 'true') {
        var caja = this.querySelector('[data-de-debug]');
        if (caja) {
          caja.textContent = res.error
            ? 'Sin fecha: ' + res.error
            : 'salida ' + (res.salida ? aClaveISO(res.salida) : '—') +
              ' · min ' + aClaveISO(res.min) + ' · max ' + aClaveISO(res.max) +
              ' · ' + res.motivo;
        }
      }
    }
  }

  DeliveryEstimate.calcular = calcular; // expuesta para pruebas

  if (!customElements.get('delivery-estimate')) {
    customElements.define('delivery-estimate', DeliveryEstimate);
  }
})();
